import { type MetaFunction } from '@remix-run/node'
import { metaTitleAndDescription } from '#app/helpers/metaTags.helper.ts'

export const meta: MetaFunction = () => {
  return metaTitleAndDescription({
    title: 'Clube do Tennis - Política de Privacidade',
    description:
      'Política de Privacidade do Clube do Tennis em conformidade com a LGPD',
    content_type: 'website',
  })
}

export default function PrivacyPolicyPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold text-center mb-8">
        Política de Privacidade
      </h1>

      <div className="prose prose-lg max-w-none">
        <p className="text-sm text-gray-600 mb-6">
          <strong>Última atualização:</strong>{' '}
          {new Date().toLocaleDateString('pt-BR')}
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Introdução</h2>
          <p>
            O Clube do Tennis ("nós", "nosso" ou "aplicativo") está comprometido
            em proteger sua privacidade e dados pessoais. Esta Política de
            Privacidade descreve como coletamos, usamos, armazenamos e
            protegemos suas informações pessoais em conformidade com a Lei Geral
            de Proteção de Dados (LGPD - Lei nº 13.709/2018) e demais
            legislações aplicáveis.
          </p>
          <p>
            Ao utilizar nosso aplicativo, você concorda com as práticas
            descritas nesta política. Caso não concorde, solicitamos que não
            utilize nossos serviços.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            2. Dados Pessoais Coletados
          </h2>

          <h3 className="text-xl font-medium mb-3">
            2.1 Dados fornecidos diretamente por você:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Dados de identificação:</strong> Nome completo, nome de
              usuário, e-mail
            </li>
            <li>
              <strong>Dados pessoais:</strong> Data de nascimento, gênero,
              nacionalidade
            </li>
            <li>
              <strong>Dados de localização:</strong> CEP, endereço, cidade,
              estado, coordenadas geográficas (latitude/longitude)
            </li>
            <li>
              <strong>Dados de perfil:</strong> Foto de perfil, nível de tênis,
              preferências de jogo
            </li>
            <li>
              <strong>Dados de autenticação:</strong> Senha (criptografada),
              tokens de acesso
            </li>
          </ul>

          <h3 className="text-xl font-medium mb-3">
            2.2 Dados coletados automaticamente:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Dados de uso:</strong> Histórico de partidas, avaliações,
              mensagens, notificações
            </li>
            <li>
              <strong>Dados de dispositivo:</strong> Identificador único do
              dispositivo, tipo de dispositivo, sistema operacional
            </li>
            <li>
              <strong>Dados de localização:</strong> Coordenadas GPS (quando
              autorizado)
            </li>
            <li>
              <strong>Dados de sessão:</strong> Tokens de acesso, data/hora de
              login, atividade na plataforma
            </li>
            <li>
              <strong>Dados de performance:</strong> Logs de erro, métricas de
              uso
            </li>
          </ul>

          <h3 className="text-xl font-medium mb-3">2.3 Dados de terceiros:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Provedores de autenticação:</strong> Dados básicos de
              perfil quando você se conecta via Google, Facebook ou Apple
            </li>
            <li>
              <strong>Google Maps:</strong> Informações de localização para
              funcionalidades de mapas
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            3. Finalidades do Tratamento
          </h2>
          <p>Utilizamos seus dados pessoais para as seguintes finalidades:</p>

          <h3 className="text-xl font-medium mb-3">
            3.1 Execução do contrato e prestação do serviço:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Criar e gerenciar sua conta de usuário</li>
            <li>Conectar você com outros jogadores de tênis</li>
            <li>Facilitar o agendamento e organização de partidas</li>
            <li>Processar pagamentos e transações (quando aplicável)</li>
            <li>Fornecer suporte ao cliente</li>
          </ul>

          <h3 className="text-xl font-medium mb-3">3.2 Legítimo interesse:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Melhorar nossos serviços e funcionalidades</li>
            <li>Prevenir fraudes e garantir a segurança da plataforma</li>
            <li>Analisar padrões de uso para otimização</li>
            <li>Desenvolver novos recursos</li>
          </ul>

          <h3 className="text-xl font-medium mb-3">3.3 Consentimento:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Envio de notificações push personalizadas</li>
            <li>Compartilhamento de dados com parceiros (quando autorizado)</li>
            <li>Uso de dados para marketing direcionado</li>
            <li>Coleta de dados de localização precisos</li>
          </ul>

          <h3 className="text-xl font-medium mb-3">
            3.4 Cumprimento de obrigação legal:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Atender solicitações de autoridades competentes</li>
            <li>Cumprir obrigações fiscais e contábeis</li>
            <li>Manter registros conforme exigido por lei</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            4. Compartilhamento de Dados
          </h2>
          <p>
            Seus dados pessoais podem ser compartilhados nas seguintes
            situações:
          </p>

          <h3 className="text-xl font-medium mb-3">4.1 Com outros usuários:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Nome de usuário e foto de perfil (visível para outros jogadores)
            </li>
            <li>Nível de tênis e preferências de jogo</li>
            <li>Localização geral (cidade/estado) para facilitar conexões</li>
          </ul>

          <h3 className="text-xl font-medium mb-3">
            4.2 Com prestadores de serviços:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Provedores de nuvem:</strong> Para armazenamento seguro
              dos dados
            </li>
            <li>
              <strong>Provedores de pagamento:</strong> Para processamento de
              transações
            </li>
          </ul>

          <h3 className="text-xl font-medium mb-3">4.3 Com autoridades:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Quando exigido por lei ou ordem judicial</li>
            <li>Para proteger nossos direitos legais</li>
            <li>Para investigar atividades suspeitas</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            5. Segurança dos Dados
          </h2>
          <p>
            Implementamos medidas técnicas e organizacionais para proteger seus
            dados:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Criptografia:</strong> Dados sensíveis são criptografados
              em trânsito e em repouso
            </li>
            <li>
              <strong>Controle de acesso:</strong> Acesso restrito aos dados
              baseado em necessidade
            </li>
            <li>
              <strong>Monitoramento:</strong> Sistemas de monitoramento contínuo
              para detectar atividades suspeitas
            </li>
            <li>
              <strong>Backup seguro:</strong> Cópias de segurança regulares e
              seguras
            </li>
            <li>
              <strong>Atualizações de segurança:</strong> Manutenção regular de
              sistemas e aplicações
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            6. Seus Direitos (LGPD)
          </h2>
          <p>Conforme a LGPD, você possui os seguintes direitos:</p>

          <h3 className="text-xl font-medium mb-3">
            6.1 Direitos fundamentais:
          </h3>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Confirmação e acesso:</strong> Saber se tratamos seus
              dados e acessá-los
            </li>
            <li>
              <strong>Correção:</strong> Corrigir dados incompletos, inexatos ou
              desatualizados
            </li>
            <li>
              <strong>Anonimização, bloqueio ou eliminação:</strong> Solicitar a
              remoção de dados desnecessários
            </li>
            <li>
              <strong>Portabilidade:</strong> Solicitar a transferência de seus
              dados
            </li>
            <li>
              <strong>Eliminação:</strong> Solicitar a exclusão de dados
              tratados com consentimento
            </li>
            <li>
              <strong>Informação:</strong> Obter informações sobre
              compartilhamento de dados
            </li>
            <li>
              <strong>Revogação do consentimento:</strong> Retirar seu
              consentimento a qualquer momento
            </li>
          </ul>

          <h3 className="text-xl font-medium mb-3">
            6.2 Como exercer seus direitos:
          </h3>
          <p>
            Para exercer qualquer um desses direitos, entre em contato conosco
            através de:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>E-mail: privacidade@clubedotennis.com</li>
            <li>Formulário de contato no aplicativo</li>
            <li>Central de ajuda no aplicativo</li>
          </ul>
          <p>
            Responderemos sua solicitação em até 15 (quinze) dias úteis,
            conforme estabelecido pela LGPD.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. Retenção de Dados</h2>
          <p>Mantemos seus dados pessoais pelo tempo necessário para:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Conta ativa:</strong> Enquanto sua conta estiver ativa e
              você utilizar nossos serviços
            </li>
            <li>
              <strong>Obrigações legais:</strong> Conforme exigido por lei (ex:
              registros fiscais por 5 anos)
            </li>
            <li>
              <strong>Resolução de disputas:</strong> Para resolver disputas e
              fazer cumprir acordos
            </li>
            <li>
              <strong>Segurança:</strong> Para prevenir fraudes e garantir a
              segurança da plataforma
            </li>
          </ul>
          <p>
            Após a exclusão da conta, alguns dados podem ser mantidos de forma
            anonimizada para fins estatísticos e de melhoria dos serviços.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            8. Cookies e Tecnologias Similares
          </h2>
          <p>Utilizamos cookies e tecnologias similares para:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Autenticação:</strong> Manter sua sessão ativa
            </li>
            <li>
              <strong>Preferências:</strong> Lembrar suas configurações
            </li>
            <li>
              <strong>Analytics:</strong> Entender como você usa nosso
              aplicativo
            </li>
            <li>
              <strong>Funcionalidade:</strong> Garantir o funcionamento adequado
              dos recursos
            </li>
          </ul>
          <p>
            Você pode gerenciar cookies através das configurações do seu
            navegador, mas isso pode afetar a funcionalidade do aplicativo.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            9. Transferência Internacional
          </h2>
          <p>
            Alguns de nossos prestadores de serviços podem estar localizados
            fora do Brasil. Quando isso ocorrer, garantimos que:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              O país de destino ofereça nível adequado de proteção de dados
            </li>
            <li>
              Exista acordo de transferência adequado (cláusulas contratuais
              padrão)
            </li>
            <li>
              Você tenha dado consentimento específico para a transferência
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. Menores de Idade</h2>
          <p>
            Nossos serviços são destinados a pessoas com 18 anos ou mais. Não
            coletamos intencionalmente dados pessoais de menores de 18 anos. Se
            tomarmos conhecimento de que coletamos dados de um menor, tomaremos
            medidas para excluir essas informações.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            11. Alterações nesta Política
          </h2>
          <p>
            Podemos atualizar esta Política de Privacidade periodicamente.
            Quando isso ocorrer:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Notificaremos você através do aplicativo ou por e-mail</li>
            <li>
              Atualizaremos a data de "última modificação" no topo desta página
            </li>
            <li>
              Alterações significativas serão comunicadas com 30 dias de
              antecedência
            </li>
          </ul>
          <p>
            O uso continuado de nossos serviços após as alterações constitui
            aceitação da nova política.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            12. Encarregado de Dados (DPO)
          </h2>
          <p>
            Nomeamos um Encarregado de Dados (Data Protection Officer - DPO)
            para supervisionar o cumprimento desta política e da LGPD:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>E-mail:</strong> contato@clubedotennis.com.br
            </li>
            <li>
              <strong>Função:</strong> Supervisar o tratamento de dados pessoais
            </li>
            <li>
              <strong>Responsabilidades:</strong> Orientar funcionários, receber
              comunicações da ANPD, orientar titulares
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">13. Contato</h2>
          <p>
            Para questões relacionadas a esta Política de Privacidade ou ao
            tratamento de seus dados pessoais:
          </p>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Clube do Tennis</h3>
            <ul className="space-y-2">
              <li>
                <strong>E-mail geral:</strong> contato@clubedotennis.com
              </li>
              <li>
                <strong>E-mail de privacidade:</strong>{' '}
                contato@clubedotennis.com.br
              </li>
              <li>
                <strong>DPO:</strong> contato@clubedotennis.com.br
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            14. Autoridade Nacional de Proteção de Dados (ANPD)
          </h2>
          <p>
            Você tem o direito de apresentar reclamações à Autoridade Nacional
            de Proteção de Dados (ANPD) caso considere que o tratamento de seus
            dados pessoais viola a legislação aplicável.
          </p>
          <p>
            <strong>ANPD:</strong> www.gov.br/anpd
          </p>
        </section>

        <div className="mt-12 p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">
            Resumo dos Seus Direitos
          </h3>
          <p className="text-sm">
            Você tem o direito de acessar, corrigir, excluir, portar e limitar o
            tratamento de seus dados pessoais. Você também pode retirar seu
            consentimento a qualquer momento. Para exercer esses direitos, entre
            em contato conosco através dos canais indicados acima.
          </p>
        </div>
      </div>
    </div>
  )
}
